import { useMemo } from 'react'

import { useMarkets } from '../../../hooks/useMarkets.js'
import { isDrayNowHost } from '../../../lib/config/multitenancy.js'
import {
  buildExplicitSort,
  getDefaultSort,
} from '../utils/marketplaceConstants.js'
import { mergeFilterQueryProps } from '../components/MarketplaceFilters/filterTransformers.js'
import { FILTER_TYPES } from '../components/MarketplaceFilters/filterDefinitions.js'
import { useMarketplaceParams } from './useMarketplaceParams.js'

/**
 * useMarketplaceQueryParams
 *
 * The request params for the marketplace list and counts queries. Kept out of
 * useMarketplaceParams because resolving filters needs the markets list, and
 * only the components that fetch should observe it.
 *
 * @returns {Object}
 * @property {Object} adminFilterQueryProps - URL filters resolved to query props
 * @property {Object} apiParams - Params for the orders/trips list queries
 * @property {boolean} apiParamsReady - False while the params still depend on
 *   markets that have not loaded; callers keep their queries disabled and show
 *   a loader until then
 */
export const useMarketplaceQueryParams = () => {
  const {
    markets,
    category,
    page,
    perPage,
    search,
    listType,
    sortState,
    adminFilters,
    hasExplicitSort,
  } = useMarketplaceParams()
  const { availableMarkets, isLoading: marketsLoading } = useMarkets()

  const isDrayNow = useMemo(() => isDrayNowHost(), [])

  const adminFilterQueryProps = useMemo(
    () =>
      mergeFilterQueryProps(adminFilters, {
        selectedMarkets: markets,
        availableMarkets,
      }),
    [adminFilters, markets, availableMarkets],
  )

  // The date filter is the only one that resolves against market timezones, so
  // it is the only one whose params are wrong before markets land. Fetching on
  // an empty markets list would send browser-local day boundaries, then refetch
  // the corrected ones — showing the wrong day's rows in between.
  const apiParamsReady = useMemo(
    () =>
      !marketsLoading ||
      !adminFilters.some((filter) => filter.key === FILTER_TYPES.DATE),
    [marketsLoading, adminFilters],
  )

  // When the user has explicitly sorted (a `sort` URL param is present), send
  // the clicked column's full sort — single-key for most columns, multi-key for
  // the ModalView driver/completed columns (see MULTI_KEY_SORTS). Otherwise send
  // the category's full default sort — which is multi-key for `overview`
  // (severity → category → appointment) and DESC for closed/canceled (legacy
  // Marketplace.defaultSort semantics).
  const apiParams = useMemo(() => {
    const sort =
      hasExplicitSort && sortState.field
        ? JSON.stringify(
            buildExplicitSort(sortState.field, sortState.direction),
          )
        : JSON.stringify(getDefaultSort(category, listType, isDrayNow))

    return {
      category,
      page,
      perPage,
      marketUids: markets,
      search: search || undefined,
      sort,
      ...adminFilterQueryProps,
    }
  }, [
    category,
    listType,
    page,
    perPage,
    markets,
    search,
    sortState,
    hasExplicitSort,
    adminFilterQueryProps,
    isDrayNow,
  ])

  return { adminFilterQueryProps, apiParams, apiParamsReady }
}
